import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import { useLanguageSwitch } from '@/hooks/useLanguage';
import SharedNavbar from '@/components/feature/SharedNavbar';
import SharedFooter from '@/components/feature/SharedFooter';
import { useToast } from '@/contexts/ToastContext';
import { supabase } from '@/lib/supabaseClient';

const schema = z.object({
  email: z.string().email(),
  password: z.string().min(6),
  remember: z.boolean().optional(),
});

type FormData = z.infer<typeof schema>;

export default function LoginPage() {
  const { t } = useTranslation();
  const { login, isAuthenticated, isReseller, refreshUser } = useAuth();
  const navigate = useNavigate();
  const { currentLang } = useLanguageSwitch();
  const prefix = currentLang === 'ko' ? '/ko' : currentLang === 'bn' ? '/bn' : '';
  const { showToast } = useToast();
  const [error, setError] = useState('');

  // Covers both a Google/social redirect landing back here (consumeSsoHandoff
  // in main.tsx already applied the session from the URL fragment before
  // this component ever mounts) and simply opening /login while already
  // signed in. Staff has neither an rxk_customers nor a core_partners row,
  // so isAuthenticated (customer-or-reseller only) never goes true for it —
  // checked directly and hard-navigated, same reasoning as the password
  // login path below.
  useEffect(() => {
    (async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) return;
      const { data: roleRow } = await supabase.from('user_roles').select('role').eq('user_id', session.user.id).maybeSingle();
      if (roleRow) {
        window.location.href = '/admin';
        return;
      }
      // A brand-new Google/Facebook sign-in has no rxk_customers row yet —
      // the provider never sets the rxk_customer signup flag our own
      // register() form does, so the DB trigger that watches for that flag
      // doesn't fire for it. Create the row ourselves (own-row insert is
      // allowed by RLS) so isAuthenticated resolves true below instead of
      // silently looking logged-out. Phone isn't in what Google gives us —
      // left blank, same as any other gap in a customer's profile.
      const { data: existing } = await supabase.from('rxk_customers').select('id').eq('id', session.user.id).maybeSingle();
      if (!existing) {
        const meta = session.user.user_metadata ?? {};
        await supabase.from('rxk_customers').insert({
          id: session.user.id,
          full_name: (meta.full_name as string) || (meta.name as string) || session.user.email || 'Customer',
          email: session.user.email,
          signup_provider: session.user.app_metadata?.provider ?? 'email',
        });
        await refreshUser();
      }
    })();
  }, [refreshUser]);

  useEffect(() => {
    if (isAuthenticated) navigate(isReseller ? prefix + '/reseller' : prefix + '/my');
  }, [isAuthenticated, isReseller, navigate, prefix]);

  const handleSocialLogin = async (provider: 'google' | 'facebook' | 'apple') => {
    await supabase.auth.signInWithOAuth({
      provider,
      options: { redirectTo: window.location.origin + '/login' },
    });
  };

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { remember: false },
  });

  const onSubmit = async (data: FormData) => {
    setError('');
    const { ok, isReseller, isStaff } = await login(data.email, data.password);
    if (ok) {
      showToast(t('auth.login.success', 'Welcome back!'));
      if (isStaff) {
        // A hard navigation, not React Router's navigate() — the admin
        // panel's own permission check (UserRoleProvider) fetches the
        // session independently and hadn't caught up to this login yet by
        // the time a client-side navigate would land, which bounced
        // straight back to "/" looking logged-out. A full reload makes it
        // initialize fresh, after the session is already persisted.
        window.location.href = '/admin';
      } else {
        navigate(isReseller ? prefix + '/reseller' : prefix + '/my');
      }
    } else {
      setError(t('auth.login.error', 'Invalid email or password, or your email is not confirmed yet'));
    }
  };

  return (
    <>
      <SharedNavbar />
      <div className="w-full min-h-screen bg-[#FAFAF9] pt-24 pb-20 flex items-center justify-center px-4">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl p-6 md:p-10 border border-[#E8E8E6]">
            <h1 className="font-serif text-2xl md:text-3xl font-bold text-[#2A2A2A] mb-2">
              {t('auth.login.title', 'Sign In')}
            </h1>
            <p className="text-sm text-[#8A8A8A] mb-8">
              {t('auth.login.subtitle', 'Welcome back to Rx.K Seoul')}
            </p>

            {error && (
              <div className="mb-5 p-3 rounded-xl bg-[#7B1113]/5 border border-[#7B1113]/10 text-sm text-[#7B1113] flex items-center gap-2">
                <i className="ri-error-warning-line"></i>
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
              <div>
                <label className="block text-xs tracking-wider uppercase text-[#8A8A8A] mb-1.5">
                  {t('auth.login.email', 'Email')}
                </label>
                <input
                  type="email"
                  {...register('email')}
                  className="w-full px-4 py-3 rounded-xl border border-[#E8E8E6] text-sm text-[#2A2A2A] placeholder:text-[#8A8A8A] focus:outline-none focus:border-[#7B1113] transition-colors h-11"
                  placeholder="your@email.com"
                />
                {errors.email && (
                  <p className="mt-1 text-xs text-[#7B1113]">{errors.email.message}</p>
                )}
              </div>

              <div>
                <label className="block text-xs tracking-wider uppercase text-[#8A8A8A] mb-1.5">
                  {t('auth.login.password', 'Password')}
                </label>
                <input
                  type="password"
                  {...register('password')}
                  className="w-full px-4 py-3 rounded-xl border border-[#E8E8E6] text-sm text-[#2A2A2A] placeholder:text-[#8A8A8A] focus:outline-none focus:border-[#7B1113] transition-colors h-11"
                  placeholder="••••••••"
                />
                {errors.password && (
                  <p className="mt-1 text-xs text-[#7B1113]">{errors.password.message}</p>
                )}
              </div>

              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    {...register('remember')}
                    className="accent-[#7B1113] w-4 h-4 cursor-pointer"
                  />
                  <span className="text-xs text-[#8A8A8A]">
                    {t('auth.login.remember', 'Keep me signed in')}
                  </span>
                </label>
                <Link
                  to={prefix + '/forgot-password'}
                  className="text-xs text-[#7B1113] hover:underline"
                >
                  {t('auth.login.forgot', 'Forgot password?')}
                </Link>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-3.5 bg-[#7B1113] text-white text-xs tracking-[0.15em] uppercase rounded-full hover:bg-[#5C0D0F] transition-colors disabled:opacity-50 cursor-pointer text-center h-12"
              >
                {isSubmitting ? t('common.loading') : t('auth.login.submit', 'Sign In')}
              </button>
            </form>

            <div className="mt-8">
              <div className="flex items-center gap-3 mb-5">
                <div className="flex-1 h-px bg-[#E8E8E6]"></div>
                <span className="text-[11px] text-[#8A8A8A] uppercase tracking-wider">
                  {t('auth.login.orSocial', 'or continue with')}
                </span>
                <div className="flex-1 h-px bg-[#E8E8E6]"></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => handleSocialLogin('google')}
                  className="flex items-center justify-center gap-2 py-3 rounded-xl border border-[#E8E8E6] hover:bg-[#F0EFED] transition-colors cursor-pointer"
                >
                  <i className="ri-google-fill text-[#2A2A2A]"></i>
                  <span className="text-xs text-[#2A2A2A]">Google</span>
                </button>
                <button
                  onClick={() => handleSocialLogin('facebook')}
                  className="flex items-center justify-center gap-2 py-3 rounded-xl border border-[#E8E8E6] hover:bg-[#F0EFED] transition-colors cursor-pointer"
                >
                  <i className="ri-facebook-fill text-[#2A2A2A]"></i>
                  <span className="text-xs text-[#2A2A2A]">Facebook</span>
                </button>
              </div>
            </div>

            <p className="mt-8 text-center text-xs text-[#8A8A8A]">
              {t('auth.login.noAccount', "Don't have an account?")}{' '}
              <Link
                to={prefix + '/register'}
                className="text-[#7B1113] hover:underline font-medium"
              >
                {t('auth.login.registerLink', 'Sign up')}
              </Link>
            </p>
          </div>
        </div>
      </div>
      <SharedFooter />
    </>
  );
}